import { Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';

import { ModalComponent } from '../../components/modal/modal.component';
import { WindowService } from '../window/window.service';
import { StaticValuesUtils } from '../../utils/static-values/static-values.utils';
import { EventEmitterService } from '../event-emitter/event-emitter.service';
import { AppSettingsService } from '../app-settings/app-settings.service';

@Injectable()
export class ModalService {

   private modalCount = 1;
   private modalsStack: ModalStackItem[] = [];

   constructor(
      private windowService: WindowService,
      private location: Location,
      private router: Router,
      private eventEmitterService:EventEmitterService,
      private appSettingsService:AppSettingsService,
   ) { 
      this.handleBackNavigation();
   }

   public showModal(modal: ModalComponent | ExternalModalInterface) {
      if (modal.isOpen) {
         return;
      }
      modal.isOpen = true;
      if (!modal.modalId) {
         modal.modalId = this.modalCount++;
      }
      
      if (modal.mustLockScreen()) {
         this.windowService.lockScroll(false);
      }
      let newUrl: string = null;
      if (this.historyBackClosesPopup()) {
         newUrl = this.buildNewModalUrl(modal.modalId);
         this.router.navigateByUrl(newUrl, {replaceUrl: false});
      }
      this.modalsStack.push({
         modal,
         url: newUrl
      });      
      if(this.appSettingsService.isWebview()) this.eventEmitterService.emitEventShowModal();
      modal.onShow();
   }

   private buildNewModalUrl(modalId: number): string {
      return this.router.url.replace(/[&|?]modal=[^&]*/, '') + 
      (this.router.url.indexOf('?')>=0 ? '&' : '?') + 
      `modal=${modalId}`;
   }

   private handleBackNavigation(): void {
      if (!this.historyBackClosesPopup()) {
         return;
      }
      this.location.subscribe(locationChange => {
         try {
            if (locationChange.pop && this.modalsStack.length) {
               this.closeModal(this.lastModalInStack().modal, false);
            }
         } catch (e) {}
      });
   }

   private lastModalInStack(): ModalStackItem {
      return this.modalsStack.length > 0 ? this.modalsStack[this.modalsStack.length - 1] : null;
   }

   public closeModal(modal, doHistoryBack: boolean = true) {
      if (!modal.isOpen) {
         return;
      }
      if (this.historyBackClosesPopup() && doHistoryBack && 
            this.lastModalInStack() && this.lastModalInStack().modal === modal) {
         this.location.back();
         return;
      }
      const modalStackItemToClose: ModalStackItem = this.modalsStack.find(item => item.modal.modalId === modal.modalId);
      if (modalStackItemToClose) {
         this.modalsStack.splice(this.modalsStack.indexOf(modalStackItemToClose), 1);
      }

      modal.isOpen = false;
     
      const lastModalInStack = this.lastModalInStack();
      if (!lastModalInStack || !lastModalInStack.modal.mustLockScreen) {
         this.windowService.unlockScroll();
      }
      if (this.historyBackClosesPopup() && lastModalInStack && lastModalInStack.url && this.router.url !== lastModalInStack.url) {
         this.router.navigateByUrl(lastModalInStack.url, {replaceUrl: true});
      }

      if (this.appSettingsService.isWebview()) this.eventEmitterService.emitEventCloseModal();
      modal.onClose();
   }

   private historyBackClosesPopup() {
      return StaticValuesUtils.isWebview() || StaticValuesUtils.isMobile();
   }

   /* This method must be used when external components (like jeff components) open's a modal,
    * so new state is added to browser history and back navigation is handled to close modal.
      closeModalCallback is a callback function to trigger modal close when back navigation ocurrs  */
   public externalModalOpened(closeModalCallback: Function, mustLockScreen: boolean = true): number {
      const externalModal: ExternalModalInterface = {
         modalId: null,
         isOpen: false,
         onShow: () => {},
         mustLockScreen: () => mustLockScreen,
         onClose: closeModalCallback || (() => {})
      }
      this.showModal(externalModal);
      return externalModal.modalId;
   }

   public externalModalClosed(externalModalId: number) {
      const externalModal = this.modalsStack.find(item => item.modal.modalId === externalModalId);
      if (externalModal) {
         this.closeModal(externalModal.modal);
      }
   }
}

interface ExternalModalInterface {
   modalId: number,
   isOpen: boolean,
   onShow: () => void,
   mustLockScreen: () => boolean,
   onClose: Function,
}

interface ModalStackItem {
   modal: ModalComponent | ExternalModalInterface,
   url: string
}